import { useEffect, useState } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import gettext from '@sdk/lang/gettext';
import Picker from '@sdk/components/forms/Picker';
import Column from '@sdk/components/ui/spacing/Column';
import Text14 from '@sdk/components/ui/typography/Text14';
import useAuth from '@sdk/authentication/useAuth';
import ToggleGroup, {
  ToggleGroupOption,
} from '@sdk/components/forms/components/toggleGroup/ToggleGroup';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';

import {
  isNotificationPermissionDenied,
  isNotificationPermissionGranted,
  requestNotificationPermission,
} from '@web/common/utils/browserAPI/notification';

import type { ClientSettingsType, SetClientSettings } from '../../types';
import CollapsibleSettingsSection from '../CollapsibleSettingsSection';
import RowSpaceBetween from '../RowSpaceBetween';
import SettingsSection from '../SettingsSection';
import SettingsSwitcherRow from '../SettingsSwitcherRow';
import SmallHelpBlock from '../SmallHelpBlock';
import { WebNotificationType } from '../../types';
import { FIELD_WIDTH_420, NEWSFEED_NOTIFY_MODES } from '../../constants';
import { useUserSettings } from '../../UserSettingsContext';

import { handleNotify } from './helpers/utils';

const StyledColumn = styled(Column)`
  align-items: flex-end;
`;

enum NewsfeedNotify {
  newsfeedNotifyViaEmail = 'newsfeedNotifyViaEmail',
  newsfeedNotifyViaPush = 'newsfeedNotifyViaPush',
  newsfeedNotifyViaBrowser = 'newsfeedNotifyViaBrowser',
}

type Props = {
  clientSettings: ClientSettingsType;
  setClientSettings: SetClientSettings;
};

const NewsfeedNotification: React.FC<React.PropsWithChildren<Props>> = ({
  setClientSettings,
  clientSettings,
}) => {
  const { currentUser } = useAuth();
  const { userSettings, onUserSettingsChange } = useUserSettings();
  const { newsfeedNotify, newsfeedNotifyViaEmail, newsfeedNotifyViaPush } = userSettings;

  const initializeNotificationOptions = (): ToggleGroupOption[] => {
    return [
      {
        name: NewsfeedNotify.newsfeedNotifyViaEmail,
        label: gettext('Email'),
        value: newsfeedNotifyViaEmail,
        disabled: false,
      },
      {
        name: NewsfeedNotify.newsfeedNotifyViaPush,
        label: gettext('Mobile'),
        value: newsfeedNotifyViaPush,
        disabled: !currentUser.canReceivePush,
      },
      {
        name: NewsfeedNotify.newsfeedNotifyViaBrowser,
        label: gettext('Web browser'),
        value: clientSettings[WebNotificationType.newsfeedNotifications],
        disabled: isNotificationPermissionDenied(),
      },
    ];
  };

  const [newsfeedToggleGroupOptions, setNewsfeedToggleGroupOptions] = useState(() =>
    initializeNotificationOptions()
  );

  useEffect(() => {
    setNewsfeedToggleGroupOptions(initializeNotificationOptions());
    onUserSettingsChange({
      newsfeedNotify: handleNotify(
        newsfeedNotify,
        newsfeedNotifyViaEmail,
        newsfeedNotifyViaPush,
        clientSettings[WebNotificationType.newsfeedNotifications]
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newsfeedNotifyViaEmail, newsfeedNotifyViaPush, clientSettings]);

  const onGrantedPermission = (): void => {
    setClientSettings({ [WebNotificationType.newsfeedNotifications]: true });
    const webToggleOptions = newsfeedToggleGroupOptions.map((option) =>
      option.name === NewsfeedNotify.newsfeedNotifyViaBrowser ? { ...option, value: true } : option
    );

    setNewsfeedToggleGroupOptions(webToggleOptions);
  };

  const onDeniedPermission = (): void => {
    setClientSettings({ [WebNotificationType.newsfeedNotifications]: false });
  };

  const onChangeHandler = async ({ name, value }) => {
    const updatedToggleOptions = newsfeedToggleGroupOptions.map((option) =>
      option.name === name ? { ...option, value: value } : option
    );

    if (name === NewsfeedNotify.newsfeedNotifyViaBrowser) {
      if (!isNotificationPermissionGranted() && value) {
        await requestNotificationPermission(onGrantedPermission, onDeniedPermission);
        return;
      }

      setClientSettings({ [WebNotificationType.newsfeedNotifications]: value });
      setNewsfeedToggleGroupOptions(updatedToggleOptions);
      return;
    }

    setNewsfeedToggleGroupOptions(updatedToggleOptions);
    onUserSettingsChange({ [name]: value });
  };

  return (
    <SettingsSection pBottom={20}>
      <CollapsibleSettingsSection
        isActive={!!newsfeedNotify}
        headerItem={
          <SettingsSwitcherRow
            withSeparator
            value={!!newsfeedNotify}
            onChange={(value): void => onUserSettingsChange({ newsfeedNotify: value ? 1 : 0 })}
            subtitle={gettext(
              'Newsfeed notifications inform you of any new posts on pages you follow. By default, these notifications are displayed as badges on the Newsfeed tab.'
            )}
          />
        }
        contentItem={
          <>
            <Column pBottom={20}>
              <RowSpaceBetween>
                <Text14 themeColor={ThemeColorType.gray}>
                  {gettext('Send me additional alerts when:')}
                </Text14>
                <Picker
                  options={NEWSFEED_NOTIFY_MODES}
                  value={newsfeedNotify}
                  customWidth={FIELD_WIDTH_420}
                  onChange={(value: number): void =>
                    onUserSettingsChange({ newsfeedNotify: value })
                  }
                />
              </RowSpaceBetween>
            </Column>

            <Column pBottom={20}>
              <RowSpaceBetween>
                <Text14 themeColor={ThemeColorType.gray}>
                  {gettext('Send notification via:')}
                </Text14>
                <StyledColumn>
                  <ToggleGroup
                    options={newsfeedToggleGroupOptions}
                    onItemChange={onChangeHandler}
                  />
                  {!currentUser.canReceivePush && <SmallHelpBlock />}
                </StyledColumn>
              </RowSpaceBetween>
            </Column>
          </>
        }
      />
    </SettingsSection>
  );
};

export default NewsfeedNotification;
